import React from 'react'
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import ScreenShareRoundedIcon from '@mui/icons-material/ScreenShareRounded'
import StopScreenShareRoundedIcon from '@mui/icons-material/StopScreenShareRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'

void React

function renderSelectOptions(options = []) {
  return options.map((option) => (
    <option key={option.id} value={option.id}>
      {option.label}
    </option>
  ))
}

function resolveSelectValue(selectedValue) {
  if (typeof selectedValue === 'string' && selectedValue.trim()) {
    return selectedValue
  }

  return ''
}

export default function CallControlsOverlay({
  audioEnabled = true,
  videoEnabled = true,
  layoutId,
  railStateId,
  videoLayoutOptions = [],
  railStateOptions = [],
  onLayoutChange,
  onRailStateChange,
  onToggleAudio,
  onToggleVideo,
  onLeave,
  screenShareActive = false,
  onToggleScreenShare,
  showScreenShareButton = false,
  disableScreenShareButton = false,
  layoutMenuOpen = false,
  onToggleLayoutMenu,
  deviceSettingsOpen = false,
  onToggleDeviceSettings,
  showDeviceSettingsButton = true,
  cameraDevices = [],
  microphoneDevices = [],
  speakerDevices = [],
  selectedCameraDeviceId = '',
  selectedMicrophoneDeviceId = '',
  selectedSpeakerDeviceId = '',
  onCameraDeviceChange,
  onMicrophoneDeviceChange,
  onSpeakerDeviceChange,
  showLayoutSelector = true,
  showRailSelector = false,
  showAudioButton = true,
  showVideoButton = true,
  showLeaveButton = true,
}) {
  const showLayoutMenuButton = showLayoutSelector || showRailSelector
  // Layout + device settings are consolidated behind a single "More" control that opens one panel.
  const showMoreButton = showLayoutMenuButton || showDeviceSettingsButton
  const morePanelOpen = layoutMenuOpen || deviceSettingsOpen

  return (
    <div
      className="shared-video-controls"
      onMouseDown={(event) => {
        event.stopPropagation()
      }}
      onTouchStart={(event) => {
        event.stopPropagation()
      }}
    >
      {morePanelOpen ? (
        <div className="shared-video-controls__panel" role="dialog" aria-label="Call options">
          {showLayoutSelector ? (
            <div className="shared-video-controls__group">
              <span className="shared-video-controls__group-title">Layout</span>
              <div className="shared-video-controls__option-grid">
                {videoLayoutOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`shared-video-controls__option${
                      option.id === layoutId ? ' shared-video-controls__option--active' : ''
                    }`}
                    onClick={() => onLayoutChange?.(option.id)}
                    aria-pressed={option.id === layoutId}
                  >
                    <span className="shared-video-controls__option-title">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showRailSelector ? (
            <div className="shared-video-controls__group">
              <span className="shared-video-controls__group-title">Participant rail</span>
              <div className="shared-video-controls__option-grid shared-video-controls__option-grid--compact">
                {railStateOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`shared-video-controls__option${
                      option.id === railStateId ? ' shared-video-controls__option--active' : ''
                    }`}
                    onClick={() => onRailStateChange?.(option.id)}
                    aria-pressed={option.id === railStateId}
                  >
                    <span className="shared-video-controls__option-title">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showDeviceSettingsButton ? (
            <div className="shared-video-controls__group">
              <span className="shared-video-controls__group-title">Devices</span>
              <label className="shared-video-controls__field">
                <span>Microphone</span>
                <select
                  aria-label="Microphone device"
                  value={resolveSelectValue(selectedMicrophoneDeviceId)}
                  onChange={(event) => onMicrophoneDeviceChange?.(event.target.value)}
                >
                  <option value="">System default</option>
                  {renderSelectOptions(microphoneDevices)}
                </select>
              </label>

              <label className="shared-video-controls__field">
                <span>Camera</span>
                <select
                  aria-label="Camera device"
                  value={resolveSelectValue(selectedCameraDeviceId)}
                  onChange={(event) => onCameraDeviceChange?.(event.target.value)}
                >
                  <option value="">System default</option>
                  {renderSelectOptions(cameraDevices)}
                </select>
              </label>

              <label className="shared-video-controls__field">
                <span>Speaker</span>
                <select
                  aria-label="Speaker device"
                  value={resolveSelectValue(selectedSpeakerDeviceId)}
                  onChange={(event) => onSpeakerDeviceChange?.(event.target.value)}
                >
                  <option value="">System default</option>
                  {renderSelectOptions(speakerDevices)}
                </select>
              </label>
            </div>
          ) : null}
        </div>
      ) : null}

      {showAudioButton ? (
        <button
          type="button"
          className={`shared-video-controls__button${
            audioEnabled ? '' : ' shared-video-controls__button--muted'
          }`}
          onClick={onToggleAudio}
          aria-label={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {audioEnabled ? <MicRoundedIcon sx={{ fontSize: 20 }} /> : <MicOffRoundedIcon sx={{ fontSize: 20 }} />}
        </button>
      ) : null}

      {showVideoButton ? (
        <button
          type="button"
          className={`shared-video-controls__button${
            videoEnabled ? '' : ' shared-video-controls__button--muted'
          }`}
          onClick={onToggleVideo}
          aria-label={videoEnabled ? 'Turn camera off' : 'Turn camera on'}
        >
          {videoEnabled ? (
            <VideocamRoundedIcon sx={{ fontSize: 20 }} />
          ) : (
            <VideocamOffRoundedIcon sx={{ fontSize: 20 }} />
          )}
        </button>
      ) : null}

      {showScreenShareButton ? (
        <button
          type="button"
          className={`shared-video-controls__button shared-video-controls__button--labeled${
            screenShareActive
              ? ' shared-video-controls__button--sharing'
              : ''
          }`}
          onClick={onToggleScreenShare}
          aria-label={screenShareActive ? 'Stop screen sharing' : 'Share screen'}
          aria-pressed={screenShareActive}
          disabled={disableScreenShareButton}
        >
          {screenShareActive ? (
            <StopScreenShareRoundedIcon sx={{ fontSize: 20 }} />
          ) : (
            <ScreenShareRoundedIcon sx={{ fontSize: 20 }} />
          )}
          <span className="shared-video-controls__button-label">
            {screenShareActive ? 'Stop share' : 'Share screen'}
          </span>
        </button>
      ) : null}

      {showMoreButton ? (
        <button
          type="button"
          className={`shared-video-controls__button${
            morePanelOpen ? ' shared-video-controls__button--active' : ''
          }`}
          onClick={showDeviceSettingsButton ? onToggleDeviceSettings : onToggleLayoutMenu}
          aria-label={morePanelOpen ? 'Close options' : 'More options'}
          aria-expanded={morePanelOpen}
        >
          <TuneRoundedIcon sx={{ fontSize: 20 }} />
        </button>
      ) : null}

      {showLeaveButton ? (
        <button
          type="button"
          className="shared-video-controls__button shared-video-controls__button--danger"
          onClick={onLeave}
          aria-label="Leave call"
        >
          <CallEndRoundedIcon sx={{ fontSize: 20 }} />
        </button>
      ) : null}
    </div>
  )
}

CallControlsOverlay.displayName = 'CallControlsOverlay'
